import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Container, Row, Col, Card, CardBody, Badge, Button, Input, InputGroup, Spinner, Table } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import L from "leaflet";
import { useLocation } from "react-router-dom";
import "leaflet/dist/leaflet.css";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useSignalRNotifications } from "../../context/SignalRNotificationContext";

// Radar pulse styling
const liveMapPulseStyle = `
  @keyframes pulseRadarMarker {
    0% { transform: scale(0.6); opacity: 0.9; }
    100% { transform: scale(2.3); opacity: 0; }
  }
  @keyframes ripplePickup {
    0% { transform: scale(0.8); opacity: 0.8; }
    100% { transform: scale(1.8); opacity: 0; }
  }
`;

// Helper component to auto-fit or pan map on coordinate updates
const MapController = ({ points, center, followDriver }) => {
  const map = useMap();

  useEffect(() => {
    if (followDriver && center && !isNaN(center[0]) && !isNaN(center[1])) {
      map.panTo(center, { animate: true, duration: 1 });
      return;
    }

    if (!points || points.length === 0) return;
    try {
      const validPoints = points.filter(
        p => Array.isArray(p) && !isNaN(p[0]) && !isNaN(p[1]) && p[0] !== 0 && p[1] !== 0
      );
      if (validPoints.length === 1) {
        map.setView(validPoints[0], 14);
      } else if (validPoints.length > 1) {
        const bounds = L.latLngBounds(validPoints);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      }
    } catch (e) {
      console.warn("MapController bounds fitting error:", e);
    }
  }, [points, center, followDriver, map]);

  return null;
};

// Custom Marker Icons
const createPinIcon = (color, label, iconClass) => {
  return L.divIcon({
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; transform: translate(-50%, -100%);">
        <div style="
          background-color: ${color};
          color: white;
          width: 34px;
          height: 34px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.35);
          border: 2.5px solid #ffffff;
        ">
          <div style="transform: rotate(45deg); font-size: 14px; font-weight: bold;">
            ${iconClass ? `<i class="${iconClass}"></i>` : label}
          </div>
        </div>
        <div style="
          width: 8px;
          height: 8px;
          background: rgba(0,0,0,0.25);
          border-radius: 50%;
          margin-top: 2px;
        "></div>
      </div>
    `,
    className: "custom-leaflet-pin",
    iconSize: [34, 44],
    iconAnchor: [17, 44],
  });
};

const getVehicleMarkerIcon = (isLive = true) => {
  return L.divIcon({
    html: `
      <div style="position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; transform: translate(-50%, -50%);">
        ${isLive ? `
          <div style="
            position: absolute;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background-color: #2563eb;
            opacity: 0.35;
            animation: pulseRadarMarker 1.6s infinite ease-out;
          "></div>
        ` : ''}
        <div style="
          width: 36px;
          height: 36px;
          background-color: #1e40af;
          border: 2.5px solid #ffffff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 17px;
          box-shadow: 0 4px 14px rgba(30, 64, 175, 0.55);
          z-index: 10;
        ">
          <i class="fas fa-truck"></i>
        </div>
      </div>
    `,
    className: "custom-driver-leaflet-icon",
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
};

const LiveTripTracking = () => {
  document.title = "Live Trip Tracking & Dispatch Telemetry | Kirayago";

  const location = useLocation();
  const { subscribeToNewBookings } = useSignalRNotifications();

  // Booking list state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("ACTIVE"); // 'ACTIVE', 'ONGOING', 'ALL'

  // Active Selected Booking State
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [driverCoordinates, setDriverCoordinates] = useState(null);
  const [lastSignalTime, setLastSignalTime] = useState(null);
  const [followDriver, setFollowDriver] = useState(false);
  const [isSocketActive, setIsSocketActive] = useState(false);
  const [roadPolyline, setRoadPolyline] = useState([]);

  const signalRRef = useRef(null);

  const BASE_URL = API_WEB_URLS.BASE || "https://apikirayago.shinewellsofttech.co.in/api/V1/";

  // Fetch all bookings from API
  const fetchBookings = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}Masters/0/token/BookingMaster/Id/0`);
      if (resp.data && resp.data.data) {
        const list = resp.data.data.DataList || resp.data.data.dataList || [];
        const sorted = [...list].sort((a, b) => Number(b.Id || 0) - Number(a.Id || 0));
        setBookings(sorted);

        // If URL has ?id=123, select that booking
        const searchParams = new URLSearchParams(location.search);
        const urlId = searchParams.get("id");

        if (urlId) {
          const match = list.find(b => String(b.Id) === String(urlId));
          if (match) {
            setSelectedBooking(match);
            return;
          }
        }

        // Default to first ongoing or assigned booking if none currently selected
        if (!selectedBooking && list.length > 0) {
          const activeRide = list.find(b => Number(b.F_StatusMaster) >= 2 && Number(b.F_StatusMaster) <= 16) || list[0];
          setSelectedBooking(activeRide);
        }
      }
    } catch (err) {
      console.error("Error fetching bookings for Live Tracking:", err);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, location.search, selectedBooking]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Subscribe to new booking broadcasts
  useEffect(() => {
    const unsub = subscribeToNewBookings(() => {
      fetchBookings();
    });
    return () => unsub();
  }, [subscribeToNewBookings, fetchBookings]);

  // Handle SignalR connection for Live Coordinates Stream
  useEffect(() => {
    const hubUrl = BASE_URL.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub");

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    connection.on("DriverLocationUpdated", (data) => {
      const currentBookingId = selectedBooking?.Id;
      if (currentBookingId && String(data.bookingId || data.BookingId) === String(currentBookingId)) {
        const lat = parseFloat(data.latitude || data.Latitude);
        const lng = parseFloat(data.longitude || data.Longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setDriverCoordinates([lat, lng]);
          setLastSignalTime(new Date().toLocaleTimeString());
        }
      }
    });

    connection.on("DriverLiveLocationUpdated", (data) => {
      const currentBookingId = selectedBooking?.Id;
      const currentDriverId = selectedBooking?.F_VehicleMaster;
      
      const matchBooking = currentBookingId && String(data.bookingId || data.BookingId) === String(currentBookingId);
      const matchDriver = currentDriverId && String(data.driverId || data.DriverId) === String(currentDriverId);

      if (matchBooking || matchDriver) {
        const lat = parseFloat(data.latitude || data.Latitude);
        const lng = parseFloat(data.longitude || data.Longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          setDriverCoordinates([lat, lng]);
          setLastSignalTime(new Date().toLocaleTimeString());
        }
      }
    });

    connection.on("BookingUpdated", (bId) => {
      if (selectedBooking && String(selectedBooking.Id) === String(bId)) {
        fetchBookings();
      }
    });

    connection.start()
      .then(() => {
        setIsSocketActive(true);
        connection.invoke("RegisterAdmin").catch(console.warn);
        if (selectedBooking?.Id) {
          connection.invoke("JoinBookingGroup", String(selectedBooking.Id)).catch(console.warn);
        }
      })
      .catch((err) => {
        console.warn("[LiveTripTracking] SignalR connection failed:", err);
        setIsSocketActive(false);
      });

    signalRRef.current = connection;

    return () => {
      if (signalRRef.current) {
        signalRRef.current.stop().catch(() => {});
      }
    };
  }, [BASE_URL, selectedBooking, fetchBookings]);

  // When selected booking changes, re-sync group & initial coordinates
  useEffect(() => {
    if (selectedBooking) {
      const initLat = parseFloat(selectedBooking.DriverLatitude || selectedBooking.CurrentLatitude);
      const initLng = parseFloat(selectedBooking.DriverLongitude || selectedBooking.CurrentLongitude);

      if (!isNaN(initLat) && !isNaN(initLng) && initLat !== 0 && initLng !== 0) {
        setDriverCoordinates([initLat, initLng]);
        setLastSignalTime("Loaded from system");
      } else {
        // Do NOT fake driver location to pickup address
        setDriverCoordinates(null);
        setLastSignalTime("Awaiting driver GPS ping");
      }

      if (signalRRef.current && isSocketActive) {
        signalRRef.current.invoke("JoinBookingGroup", String(selectedBooking.Id)).catch(console.warn);
      }
    }
  }, [selectedBooking, isSocketActive]);

  // Compute map coordinates for points & polylines
  const pickupPoint = useMemo(() => {
    const lat = parseFloat(selectedBooking?.PickupLatitude);
    const lng = parseFloat(selectedBooking?.PickupLongitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [selectedBooking]);

  const dropPoint = useMemo(() => {
    const lat = parseFloat(selectedBooking?.Stop1Latitude || selectedBooking?.DropLatitude);
    const lng = parseFloat(selectedBooking?.Stop1Longitude || selectedBooking?.DropLongitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [selectedBooking]);

  const stop2Point = useMemo(() => {
    const lat = parseFloat(selectedBooking?.Stop2Latitude);
    const lng = parseFloat(selectedBooking?.Stop2Longitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [selectedBooking]);

  const stop3Point = useMemo(() => {
    const lat = parseFloat(selectedBooking?.Stop3Latitude);
    const lng = parseFloat(selectedBooking?.Stop3Longitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [selectedBooking]);

  const plannedRoute = useMemo(() => {
    const list = [];
    if (pickupPoint) list.push(pickupPoint);
    if (stop2Point) list.push(stop2Point);
    if (stop3Point) list.push(stop3Point);
    if (dropPoint) list.push(dropPoint);
    return list;
  }, [pickupPoint, stop2Point, stop3Point, dropPoint]);

  // Fetch real road route geometry via OSRM
  useEffect(() => {
    let isCancelled = false;
    const fetchRoadGeometry = async () => {
      if (!pickupPoint || !dropPoint) {
        setRoadPolyline([]);
        return;
      }

      const waypoints = [
        `${pickupPoint[1]},${pickupPoint[0]}`,
        ...(stop2Point ? [`${stop2Point[1]},${stop2Point[0]}`] : []),
        ...(stop3Point ? [`${stop3Point[1]},${stop3Point[0]}`] : []),
        `${dropPoint[1]},${dropPoint[0]}`
      ].join(';');

      try {
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson`);
        const json = await res.json();
        if (!isCancelled && json && json.code === 'Ok' && json.routes && json.routes.length > 0) {
          const coords = json.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          setRoadPolyline(coords);
          return;
        }
      } catch (err) {
        console.warn("OSRM road route fetch error, falling back to direct points:", err);
      }

      if (!isCancelled) {
        const fallback = [
          pickupPoint,
          ...(stop2Point ? [stop2Point] : []),
          ...(stop3Point ? [stop3Point] : []),
          dropPoint
        ];
        setRoadPolyline(fallback);
      }
    };

    fetchRoadGeometry();
    return () => {
      isCancelled = true;
    };
  }, [pickupPoint, stop2Point, stop3Point, dropPoint]);

  const allMapPoints = useMemo(() => {
    const list = [];
    if (roadPolyline.length > 0) {
      list.push(...roadPolyline);
    } else {
      if (pickupPoint) list.push(pickupPoint);
      if (stop2Point) list.push(stop2Point);
      if (stop3Point) list.push(stop3Point);
      if (dropPoint) list.push(dropPoint);
    }
    if (driverCoordinates) list.push(driverCoordinates);
    return list;
  }, [roadPolyline, pickupPoint, stop2Point, stop3Point, dropPoint, driverCoordinates]);

  // Filter Bookings for Left Panel
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      const statusId = Number(b.F_StatusMaster || 0);
      let matchFilter = true;

      if (filterType === "ONGOING") {
        matchFilter = statusId >= 2 && statusId <= 16;
      } else if (filterType === "ACTIVE") {
        matchFilter = statusId >= 1 && statusId <= 16;
      }

      if (!matchFilter) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const idMatch = String(b.Id).includes(q);
        const custMatch = (b.UserName || b.SenderName || "").toLowerCase().includes(q);
        const driverMatch = (b.DriverName || "").toLowerCase().includes(q);
        const locMatch = (b.PickupAddress || "").toLowerCase().includes(q) || (b.Stop1Address || "").toLowerCase().includes(q);
        return idMatch || custMatch || driverMatch || locMatch;
      }
      return true;
    });
  }, [bookings, filterType, searchQuery]);

  const defaultCenter = driverCoordinates || pickupPoint || [26.2389, 73.0243];

  return (
    <React.Fragment>
      <style dangerouslySetInnerHTML={{ __html: liveMapPulseStyle }} />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Operations & Dispatch" breadcrumbItem="Live Trip Tracking Command Room" />

          {/* Top Real-Time Status Bar */}
          <Row className="mb-3">
            <Col xs={12}>
              <Card className="border-0 shadow-sm bg-gradient-dark text-white mb-0" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" }}>
                <CardBody className="p-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="avatar-sm bg-primary bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center text-primary">
                        <i className="fas fa-satellite-dish font-size-22"></i>
                      </div>
                      <div>
                        <h5 className="text-white mb-0 fw-bold">Live Fleet GPS & Telemetry Engine</h5>
                        <p className="text-muted font-size-12 mb-0">Real-time driver location stream via SignalR WebSockets</p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge color={isSocketActive ? "success" : "warning"} className="px-3 py-2 font-size-12 rounded-pill d-flex align-items-center gap-1.5">
                        <span className={`spinner-grow spinner-grow-sm ${isSocketActive ? "text-white" : "text-dark"}`} style={{ width: 8, height: 8 }}></span>
                        {isSocketActive ? "SignalR Socket Online" : "Connecting to Hub..."}
                      </Badge>
                      <Button color="light" size="sm" onClick={fetchBookings} className="rounded-pill px-3">
                        <i className="fas fa-sync-alt me-1 font-size-11"></i> Refresh Fleet
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* 3-Column Main Control Room Layout */}
          <Row className="g-3">
            {/* Left Column: Active Rides Selector (3 cols) */}
            <Col lg={3} md={4}>
              <Card className="border-0 shadow-sm h-100" style={{ minHeight: "680px" }}>
                <CardBody className="p-3 d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
                      <i className="fas fa-list-ul me-2 text-primary"></i> Active Rides ({filteredBookings.length})
                    </h6>
                  </div>

                  {/* Filter tabs */}
                  <div className="btn-group w-100 mb-2 p-1 bg-light rounded" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${filterType === "ACTIVE" ? "btn-primary shadow-sm" : "btn-light text-muted"}`}
                      onClick={() => setFilterType("ACTIVE")}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${filterType === "ONGOING" ? "btn-primary shadow-sm" : "btn-light text-muted"}`}
                      onClick={() => setFilterType("ONGOING")}
                    >
                      On Trip
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${filterType === "ALL" ? "btn-primary shadow-sm" : "btn-light text-muted"}`}
                      onClick={() => setFilterType("ALL")}
                    >
                      All
                    </button>
                  </div>

                  {/* Search box */}
                  <InputGroup size="sm" className="mb-3">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="fas fa-search text-muted font-size-12"></i>
                    </span>
                    <Input
                      placeholder="Search by ID, customer, driver..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="border-start-0"
                    />
                  </InputGroup>

                  {/* Rides List */}
                  <div className="flex-grow-1 overflow-auto" style={{ maxHeight: "540px" }}>
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="sm" />
                        <p className="text-muted small mt-2">Loading active rides...</p>
                      </div>
                    ) : filteredBookings.length > 0 ? (
                      <div className="d-flex flex-column gap-2">
                        {filteredBookings.map((b) => {
                          const isSelected = selectedBooking && String(selectedBooking.Id) === String(b.Id);
                          const statusId = Number(b.F_StatusMaster || 0);

                          let badgeColor = "info";
                          if (statusId >= 2 && statusId <= 16) badgeColor = "warning";
                          if (statusId === 17 || statusId === 18) badgeColor = "success";
                          if (statusId === 19) badgeColor = "danger";

                          return (
                            <div
                              key={b.Id}
                              onClick={() => setSelectedBooking(b)}
                              className={`p-2.5 rounded border cursor-pointer transition-all ${
                                isSelected
                                  ? "bg-soft-primary border-primary shadow-sm"
                                  : "bg-light border-light hover-shadow"
                              }`}
                              style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="fw-bold text-dark font-size-13">#{b.Id}</span>
                                <Badge color={badgeColor} pill className="font-size-10 px-2 py-0.5">
                                  {statusId === 1 ? "Pending" : statusId === 2 ? "Assigned" : statusId === 3 ? "Heading to Pickup" : statusId === 4 ? "Reached Pickup" : statusId >= 5 && statusId <= 16 ? "In Transit" : statusId === 18 ? "Completed" : statusId === 19 ? "Cancelled" : (b.Status || b.StatusName || "Active")}
                                </Badge>
                              </div>

                              <div className="text-truncate text-muted font-size-11 mb-1">
                                👤 <b>{b.UserName || b.SenderName || "Customer"}</b>
                              </div>

                              <div className="text-truncate text-dark font-size-11 mb-1" title={b.PickupAddress}>
                                📍 {b.PickupAddress || "Pickup"}
                              </div>

                              <div className="d-flex align-items-center justify-content-between font-size-11 pt-1 border-top border-light">
                                <span className="text-muted">
                                  🚚 {b.DriverName ? b.DriverName : <span className="text-danger">Unassigned</span>}
                                </span>
                                <span className="fw-bold text-success">
                                  ₹{(b.Price || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted small">
                        <i className="fas fa-truck-loading font-size-24 mb-2 text-secondary d-block"></i>
                        No active rides matching filter
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Center Column: Live Leaflet Map (6 cols) */}
            <Col lg={6} md={8}>
              <Card className="border-0 shadow-sm h-100" style={{ minHeight: "680px" }}>
                <CardBody className="p-0 position-relative d-flex flex-column">
                  {/* Map Header Controls */}
                  <div className="p-3 bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <h6 className="fw-bold text-dark mb-0">
                        {selectedBooking ? `Tracking Booking #${selectedBooking.Id}` : "Select a booking to track"}
                      </h6>
                      <small className="text-muted font-size-12">
                        {lastSignalTime ? `Last GPS Ping: ${lastSignalTime}` : "Waiting for coordinates"}
                      </small>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        color={followDriver ? "primary" : "outline-secondary"}
                        onClick={() => setFollowDriver(prev => !prev)}
                        className="rounded-pill px-2.5 font-size-11"
                      >
                        <i className={`fas fa-crosshairs me-1 ${followDriver ? "fa-spin" : ""}`}></i>
                        {followDriver ? "Following Vehicle" : "Follow Vehicle"}
                      </Button>
                    </div>
                  </div>

                  {/* Map Canvas */}
                  <div className="flex-grow-1" style={{ minHeight: "600px", width: "100%", position: "relative" }}>
                    <MapContainer
                      center={defaultCenter}
                      zoom={13}
                      style={{ height: "100%", width: "100%", minHeight: "600px" }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />

                      <MapController
                        points={allMapPoints}
                        center={driverCoordinates}
                        followDriver={followDriver}
                      />

                      {/* Pickup Marker */}
                      {pickupPoint && (
                        <Marker position={pickupPoint} icon={createPinIcon("#16a34a", "P", "fas fa-map-pin")}>
                          <Popup>
                            <div className="p-1">
                              <b className="text-success">🟢 Pickup Point</b>
                              <div className="small text-muted mt-1">{selectedBooking?.PickupAddress}</div>
                              {selectedBooking?.SenderName && <div className="small mt-1">Sender: <b>{selectedBooking.SenderName}</b></div>}
                            </div>
                          </Popup>
                        </Marker>
                      )}

                      {/* Multi-Stop 2 Marker */}
                      {stop2Point && (
                        <Marker position={stop2Point} icon={createPinIcon("#f59e0b", "S2", "fas fa-store")}>
                          <Popup>
                            <div className="p-1">
                              <b className="text-warning">Stop 2</b>
                              <div className="small text-muted mt-1">{selectedBooking?.Stop2Address}</div>
                            </div>
                          </Popup>
                        </Marker>
                      )}

                      {/* Multi-Stop 3 Marker */}
                      {stop3Point && (
                        <Marker position={stop3Point} icon={createPinIcon("#f59e0b", "S3", "fas fa-store")}>
                          <Popup>
                            <div className="p-1">
                              <b className="text-warning">Stop 3</b>
                              <div className="small text-muted mt-1">{selectedBooking?.Stop3Address}</div>
                            </div>
                          </Popup>
                        </Marker>
                      )}

                      {/* Drop / Destination Marker */}
                      {dropPoint && (
                        <Marker position={dropPoint} icon={createPinIcon("#dc2626", "D", "fas fa-flag-checkered")}>
                          <Popup>
                            <div className="p-1">
                              <b className="text-danger">🏁 Final Destination</b>
                              <div className="small text-muted mt-1">{selectedBooking?.Stop1Address || selectedBooking?.DropAddress}</div>
                              {selectedBooking?.Stop1ReceiverName && <div className="small mt-1">Receiver: <b>{selectedBooking.Stop1ReceiverName}</b></div>}
                            </div>
                          </Popup>
                        </Marker>
                      )}

                      {/* Live Vehicle Marker */}
                      {driverCoordinates && (
                        <Marker position={driverCoordinates} icon={getVehicleMarkerIcon(isSocketActive)}>
                          <Popup>
                            <div className="p-1">
                              <b className="text-primary">🚚 Live Vehicle Stream</b>
                              <div className="small mt-1">Driver: <b>{selectedBooking?.DriverName || "Driver"}</b></div>
                              <div className="small">Plate: <b>{selectedBooking?.VehicleNo || "-"}</b></div>
                              <div className="text-muted small mt-1">Updated: {lastSignalTime}</div>
                            </div>
                          </Popup>
                        </Marker>
                      )}

                      {/* Real Road Polyline Casing & Main Path */}
                      {(roadPolyline.length > 1 || plannedRoute.length > 1) && (
                        <>
                          <Polyline
                            positions={roadPolyline.length > 1 ? roadPolyline : plannedRoute}
                            pathOptions={{ color: "#1e3a8a", weight: 8, opacity: 0.6 }}
                          />
                          <Polyline
                            positions={roadPolyline.length > 1 ? roadPolyline : plannedRoute}
                            pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.95 }}
                          />
                        </>
                      )}

                      {/* Live Driver Polyline to Drop */}
                      {driverCoordinates && dropPoint && (
                        <>
                          <Polyline
                            positions={[driverCoordinates, dropPoint]}
                            pathOptions={{ color: "#065f46", weight: 6, opacity: 0.5, dashArray: "6, 8" }}
                          />
                          <Polyline
                            positions={[driverCoordinates, dropPoint]}
                            pathOptions={{ color: "#10b981", weight: 4, opacity: 0.95, dashArray: "6, 8" }}
                          />
                        </>
                      )}
                    </MapContainer>

                    {/* Floating GPS HUD Pill */}
                    <div style={{ position: "absolute", top: 15, right: 15, zIndex: 1000 }}>
                      <div className="bg-white shadow px-3 py-1.5 rounded-pill d-flex align-items-center gap-2 border">
                        <span className={`spinner-grow spinner-grow-sm ${driverCoordinates ? "text-success" : "text-warning"}`} style={{ width: 8, height: 8 }}></span>
                        <span className="font-size-12 fw-semibold text-dark">
                          {driverCoordinates ? (isSocketActive ? "Live GPS Connected" : "GPS Synced") : "Awaiting Driver GPS Fix"}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column: Telemetry & Dossier (3 cols) */}
            <Col lg={3} md={12}>
              {selectedBooking ? (
                <Card className="border-0 shadow-sm h-100" style={{ minHeight: "680px" }}>
                  <CardBody className="p-3 d-flex flex-column" style={{ maxHeight: "680px", overflowY: "auto" }}>
                    {/* Header: Status & Price */}
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                      <div>
                        <span className="text-muted font-size-11 d-block">BOOKING STATUS</span>
                        <Badge color="primary" className="font-size-12 px-2 py-1 mt-0.5">
                          {selectedBooking.Status || selectedBooking.StatusName || "Active"}
                        </Badge>
                      </div>
                      <div className="text-end">
                        <span className="text-muted font-size-11 d-block">TOTAL FARE</span>
                        <span className="fw-bold text-success font-size-18">
                          ₹{(selectedBooking.Price || selectedBooking.FinalAmountCustomer || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Driver Card */}
                    <Card className="border mb-3 shadow-none bg-light">
                      <CardBody className="p-2.5">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="avatar-xs bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                            <i className="fas fa-truck font-size-12"></i>
                          </div>
                          <div className="flex-grow-1 text-truncate">
                            <span className="text-muted font-size-11 d-block">DRIVER & FLEET</span>
                            <strong className="text-dark font-size-13 d-block text-truncate">
                              {selectedBooking.DriverName || "Awaiting Assignment"}
                            </strong>
                          </div>
                          {selectedBooking.DriverMobile && (
                            <a href={`tel:${selectedBooking.DriverMobile}`} className="btn btn-sm btn-success rounded-pill px-2.5 py-1">
                              <i className="fas fa-phone-alt font-size-11 me-1"></i> Call
                            </a>
                          )}
                        </div>
                        <div className="row g-2 font-size-11 text-muted">
                          <div className="col-6">
                            Vehicle: <b className="text-dark">{selectedBooking.VehicleNo || "-"}</b>
                          </div>
                          <div className="col-6">
                            Type: <b className="text-dark">{selectedBooking.VehicleName || selectedBooking.VehicleCategory || "-"}</b>
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    {/* Customer Card */}
                    <Card className="border mb-3 shadow-none bg-light">
                      <CardBody className="p-2.5">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="avatar-xs bg-success text-white rounded-circle d-flex align-items-center justify-content-center">
                            <i className="fas fa-user font-size-12"></i>
                          </div>
                          <div className="flex-grow-1 text-truncate">
                            <span className="text-muted font-size-11 d-block">CUSTOMER</span>
                            <strong className="text-dark font-size-13 d-block text-truncate">
                              {selectedBooking.UserName || selectedBooking.SenderName || "Customer"}
                            </strong>
                          </div>
                          {(selectedBooking.UserMobileNo || selectedBooking.SenderMobile) && (
                            <a href={`tel:${selectedBooking.UserMobileNo || selectedBooking.SenderMobile}`} className="btn btn-sm btn-outline-primary rounded-pill px-2.5 py-1">
                              <i className="fas fa-phone-alt font-size-11 me-1"></i> Call
                            </a>
                          )}
                        </div>
                        <div className="font-size-11 text-muted">
                          Mobile: <b className="text-dark">{selectedBooking.UserMobileNo || selectedBooking.SenderMobile || "-"}</b>
                        </div>
                      </CardBody>
                    </Card>

                    {/* Security Verification Keys */}
                    <div className="d-flex gap-2 mb-3">
                      <div className="flex-fill p-2 bg-light rounded border text-center">
                        <span className="text-muted font-size-11 d-block">START OTP</span>
                        <span className="font-monospace fw-bold text-dark font-size-14">
                          {selectedBooking.OTP || "----"}
                        </span>
                      </div>
                      <div className="flex-fill p-2 bg-light rounded border text-center">
                        <span className="text-muted font-size-11 d-block">DELIVERY OTP</span>
                        <span className="font-monospace fw-bold text-dark font-size-14">
                          {selectedBooking.DeliveryOTP || "----"}
                        </span>
                      </div>
                    </div>

                    {/* Journey Route Timeline */}
                    <div className="border-top pt-2 flex-grow-1">
                      <span className="text-muted font-size-11 fw-bold d-block mb-2">ROUTE STOPS</span>
                      <div className="position-relative ps-3" style={{ borderLeft: "2px solid #e2e8f0", marginLeft: "6px" }}>
                        {/* Pickup */}
                        <div className="mb-2.5 position-relative">
                          <span style={{ position: "absolute", left: "-19px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#16a34a" }}></span>
                          <span className="text-muted font-size-10 d-block">PICKUP</span>
                          <span className="font-size-12 text-dark fw-semibold text-wrap d-block">{selectedBooking.PickupAddress || "-"}</span>
                        </div>

                        {/* Intermediate Stops */}
                        {selectedBooking.Stop2Address && (
                          <div className="mb-2.5 position-relative">
                            <span style={{ position: "absolute", left: "-19px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#f59e0b" }}></span>
                            <span className="text-muted font-size-10 d-block">STOP 2</span>
                            <span className="font-size-12 text-dark fw-semibold text-wrap d-block">{selectedBooking.Stop2Address}</span>
                          </div>
                        )}

                        {/* Final Drop */}
                        <div className="position-relative">
                          <span style={{ position: "absolute", left: "-19px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#dc2626" }}></span>
                          <span className="text-muted font-size-10 d-block">FINAL DROP</span>
                          <span className="font-size-12 text-dark fw-semibold text-wrap d-block">{selectedBooking.Stop1Address || selectedBooking.DropAddress || "-"}</span>
                        </div>
                      </div>
                    </div>

                    {/* Live Telemetry Info */}
                    {driverCoordinates && (
                      <div className="mt-3 p-2 bg-soft-primary rounded border border-primary font-size-11">
                        <div className="d-flex justify-content-between text-dark fw-semibold mb-1">
                          <span>Latitude: {driverCoordinates[0].toFixed(5)}</span>
                          <span>Longitude: {driverCoordinates[1].toFixed(5)}</span>
                        </div>
                        <div className="text-success font-size-10 text-end">
                          <i className="fas fa-check-circle me-1"></i> SignalR streaming live
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              ) : (
                <Card className="border-0 shadow-sm h-100 d-flex align-items-center justify-content-center p-4 text-center text-muted">
                  <i className="fas fa-map-marked-alt font-size-36 mb-2 text-secondary opacity-50"></i>
                  <p className="mb-0">Select any active ride from the list on the left to inspect live telemetry.</p>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LiveTripTracking;
